import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

type Status = 'idle' | 'sending' | 'success' | 'error';
type RequiredField = 'name' | 'email' | 'organizationName' | 'message';
type FieldErrors = Partial<Record<RequiredField, string>>;

interface FormState {
  name: string;
  email: string;
  organizationName: string;
  role: string;
  phone: string;
  leagueSize: string;
  intent: string;
  timeline: string;
  message: string;
}

const INTENT_OPTIONS = [
  { value: 'book_demo', label: 'Book a demo' },
  { value: 'bring_to_league', label: 'Bring this to my league' },
  { value: 'general_question', label: 'General question' },
  { value: 'partnership', label: 'Partnership idea' },
  { value: 'product_feedback', label: 'Product feedback' },
];

const TIMELINE_OPTIONS = [
  { value: '', label: 'Not sure yet' },
  { value: 'soon', label: 'As soon as possible' },
  { value: 'this_season', label: 'This season' },
  { value: 'next_season', label: 'Next season' },
  { value: 'exploring', label: 'Just exploring' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function intentFromQuery(value: string | null) {
  if (value === 'bring_to_league') return 'bring_to_league';
  if (value === 'general_question') return 'general_question';
  if (value === 'partnership') return 'partnership';
  if (value === 'product_feedback') return 'product_feedback';
  return 'book_demo';
}

function validateForm(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim()) errors.name = 'Name is required.';
  if (!form.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_RE.test(form.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (!form.organizationName.trim()) errors.organizationName = 'League or organization is required.';
  if (!form.message.trim()) errors.message = 'Message is required.';
  return errors;
}

export function ContactPage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>(() => ({
    name: '',
    email: '',
    organizationName: '',
    role: '',
    phone: '',
    leagueSize: '',
    intent: intentFromQuery(searchParams.get('intent')),
    timeline: '',
    message: '',
  }));
  const [status, setStatus] = useState<Status>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const setField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in fieldErrors) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field as RequiredField];
        return next;
      });
    }
  };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErrMsg('Please fix the highlighted fields.');
      setStatus('error');
      return;
    }

    setStatus('sending');
    setErrMsg('');
    setFieldErrors({});
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          sourcePath: window.location.pathname,
          referrer: document.referrer,
        }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string };
      if (res.ok && data.ok) {
        setStatus('success');
      } else {
        setErrMsg(data.error ?? 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setErrMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  }

  return (
    <article className="m-article">
      <div className="eyebrow">Contact</div>
      <h1 className="h1">Bring smarter league management to your rink.</h1>
      <p className="lede">
        Tell us about your roller hockey league and what you want to improve.
        We will use this to shape the right demo and follow-up.
      </p>

      {status === 'success' ? (
        <div className="card" style={{ marginTop: 24, padding: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Message sent.</div>
          <p className="muted">Thanks for reaching out. We will get back to you soon.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate style={{ marginTop: 24 }}>
          <div className="stack" style={{ gap: 16 }}>
            <div className="grid grid--2" style={{ gap: 16 }}>
              <label className="field">
                <span className="field__label">Name</span>
                <input
                  id="contact-name"
                  className={`input ${fieldErrors.name ? 'is-invalid' : ''}`}
                  type="text"
                  autoComplete="name"
                  maxLength={120}
                  required
                  aria-invalid={fieldErrors.name ? 'true' : 'false'}
                  aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                />
                {fieldErrors.name && (
                  <span id="contact-name-error" className="field__error">{fieldErrors.name}</span>
                )}
              </label>
              <label className="field">
                <span className="field__label">Email</span>
                <input
                  id="contact-email"
                  className={`input ${fieldErrors.email ? 'is-invalid' : ''}`}
                  type="email"
                  autoComplete="email"
                  maxLength={254}
                  required
                  aria-invalid={fieldErrors.email ? 'true' : 'false'}
                  aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                />
                {fieldErrors.email && (
                  <span id="contact-email-error" className="field__error">{fieldErrors.email}</span>
                )}
              </label>
            </div>

            <div className="grid grid--2" style={{ gap: 16 }}>
              <label className="field">
                <span className="field__label">League or organization</span>
                <input
                  id="contact-organization"
                  className={`input ${fieldErrors.organizationName ? 'is-invalid' : ''}`}
                  type="text"
                  maxLength={160}
                  required
                  aria-invalid={fieldErrors.organizationName ? 'true' : 'false'}
                  aria-describedby={fieldErrors.organizationName ? 'contact-organization-error' : undefined}
                  value={form.organizationName}
                  onChange={(e) => setField('organizationName', e.target.value)}
                />
                {fieldErrors.organizationName && (
                  <span id="contact-organization-error" className="field__error">{fieldErrors.organizationName}</span>
                )}
              </label>
              <label className="field">
                <span className="field__label">Your role</span>
                <input
                  className="input"
                  type="text"
                  maxLength={120}
                  placeholder="Owner, director, captain, scorekeeper"
                  value={form.role}
                  onChange={(e) => setField('role', e.target.value)}
                />
              </label>
            </div>

            <div className="grid grid--2" style={{ gap: 16 }}>
              <label className="field">
                <span className="field__label">Phone</span>
                <input
                  className="input"
                  type="tel"
                  autoComplete="tel"
                  maxLength={40}
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field__label">League size</span>
                <input
                  className="input"
                  type="text"
                  maxLength={80}
                  placeholder="Teams, players, divisions, or events"
                  value={form.leagueSize}
                  onChange={(e) => setField('leagueSize', e.target.value)}
                />
              </label>
            </div>

            <div className="grid grid--2" style={{ gap: 16 }}>
              <label className="field">
                <span className="field__label">What can we help with?</span>
                <select className="select" value={form.intent} onChange={(e) => setField('intent', e.target.value)}>
                  {INTENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field__label">Timeline</span>
                <select className="select" value={form.timeline} onChange={(e) => setField('timeline', e.target.value)}>
                  {TIMELINE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="field">
              <span className="field__label">Message</span>
              <textarea
                id="contact-message"
                className={`textarea ${fieldErrors.message ? 'is-invalid' : ''}`}
                rows={6}
                maxLength={4000}
                required
                aria-invalid={fieldErrors.message ? 'true' : 'false'}
                aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                value={form.message}
                onChange={(e) => setField('message', e.target.value)}
                placeholder="How does your league run today, and what would you want The Scorekeeper to improve?"
                style={{ resize: 'vertical' }}
              />
              {fieldErrors.message && (
                <span id="contact-message-error" className="field__error">{fieldErrors.message}</span>
              )}
            </label>

            {status === 'error' && (
              <p role="alert" style={{ color: 'var(--color-danger, #e53e3e)', fontSize: 14 }}>{errMsg}</p>
            )}
            <button
              type="submit"
              className="btn btn--primary"
              disabled={status === 'sending'}
              style={{ alignSelf: 'flex-start' }}
            >
              {status === 'sending' ? 'Sending...' : 'Send inquiry'}
            </button>
          </div>
        </form>
      )}
    </article>
  );
}
